import { Component, forwardRef } from '@angular/core';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormControl,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { PhoneNumber } from 'src/shared/api';
import { takeUntil, filter, shareReplay, mapTo, map } from 'rxjs/operators';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import {
	parsePhoneNumber,
	PhoneNumber as Phone,
	formatNumber,
	parseNumber,
	ParsedNumber,
} from 'libphonenumber-js';
import { merge, BehaviorSubject } from 'rxjs';
import { phoneMask } from 'src/shared/constants';
import { some } from 'lodash';

@Component({
	selector: 'rpshared-primary-phone-number',
	templateUrl: './primary-phone-number.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PrimaryPhoneNumberComponent),
			multi: true,
		},
	],
})
export class PrimaryPhoneNumberComponent extends SelfDestruct
	implements ControlValueAccessor {
	private _phoneNumbers: PhoneNumber[] = [];
	private readonly _phoneNumber = new BehaviorSubject<PhoneNumber>({});

	phoneMask: {
		mask: (string | RegExp)[] | false;
	} = { mask: false };
	// Additional Form Controls
	phoneNumberInput = new FormControl('', [
		CustomValidators.isPossiblyValidPhone,
	]);

	validPhoneNumber$ = this.phoneNumberInput.valueChanges.pipe(
		filter(() => this.phoneNumberInput.valid),
		shareReplay(1)
	);

	blankPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => !val),
		mapTo('')
	);

	internationalPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => val && val.startsWith('+')),
		map((val) => parsePhoneNumber(val))
	);

	localPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => val && !val.startsWith('+')),
		map((val) => parsePhoneNumber(val, 'US'))
	);
	phoneNumber$ = merge(
		this.blankPhoneNumber$,
		this.internationalPhoneNumber$,
		this.localPhoneNumber$
	).pipe(
		map((val) => this.mapToModel(val)),
		map(({ countryCode, extension, areaCode, number }) => ({
			...this._phoneNumber.value,
			countryCode,
			extension,
			areaCode,
			number,
		})),
		shareReplay(1)
	);

	constructor() {
		super();
	}

	// model --> view
	writeValue(value: PhoneNumber[]): void {
		this._phoneNumbers = [...value];
		this.phoneMask = { mask: phoneMask };

		if (this._phoneNumbers && this._phoneNumbers.length > 0) {
			const primaryIndex = Math.max(
				0,
				this._phoneNumbers.findIndex((p) => p.isPrimary)
			);
			this._phoneNumber.next(this._phoneNumbers[primaryIndex]);
			this._phoneNumbers.splice(primaryIndex, 1);
			this.phoneNumberInput.setValue(
				this.mapFromModel(this._phoneNumber.value),
				{
					emitEvent: false,
				}
			);
		}
	}

	// view --> model
	registerOnChange(fn: (value: PhoneNumber[]) => void) {
		this.phoneNumber$
			.pipe(
				map(({ isPrimary: _isPrimary, ...p }) =>
					some(
						Object.keys(p).map(
							(key) =>
								key !== 'id' &&
								(p as { [key: string]: any })[key]
						)
					)
						? [
								...this._phoneNumbers,
								{
									...this._phoneNumber.value,
									...p,
									isPrimary: true,
								},
						  ]
						: this._phoneNumbers
				),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	private mapToModel(input: Phone | string): Partial<PhoneNumber> {
		if (input && this.isPhoneNumber(input)) {
			return <Partial<PhoneNumber>>{
				countryCode: input.countryCallingCode,
				extension: input.ext || '',
				areaCode: input.nationalNumber.slice(0, 3),
				number: input.nationalNumber.slice(3, 10),
			};
		}

		return {};
	}

	private mapFromModel(input: PhoneNumber): string {
		const countryCode = input.countryCode ? `${input.countryCode} ` : '';
		const firstThree = input.number.slice(0, 3);
		const lastFour = input.number.slice(3, 7);
		const extension = input.extension ? ` ext. ${input.extension}` : '';
		return formatNumber(
			parseNumber(
				`${countryCode} (${input.areaCode}) ${firstThree}-${lastFour}${extension}`,
				'US'
			) as ParsedNumber,
			'NATIONAL'
		);
	}

	// Type guards
	private isPhoneNumber(input: Phone | string): input is Phone {
		return (<Phone>input).countryCallingCode !== undefined;
	}
}
